import {useState} from "react";
import moment from "moment";
import {
  Card,
  Table,
  Stack,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableContainer,
  Select,
  TextField,
  FormControl,
  InputLabel,
  MenuItem,
  Grid,
  Chip,
  CircularProgress,
  Button,
} from "@mui/material";
import Page from "src/components/Page";
import Scrollbar from "src/components/Scrollbar";
import SearchNotFound from "src/components/SearchNotFound";
import ListHead from "src/components/common/ListHead";
import ListToolbar from "src/components/common/ListToolbar";
import MyPagination from "src/components/common/MyPagination";
import {asyncRequest} from "src/services/network";

const TABLE_HEAD = [
  {id: "id", label: "ID", alignRight: false},
  {id: "typeName", label: "类型", alignRight: false},
  {id: "jyt_id", label: "金银通ID", alignRight: false},
  {id: "plat_id", label: "鲸世优选UID", alignRight: false},
  {id: "amount", label: "变动数额", alignRight: false},
  {id: "before_balance", label: "变动前余额", alignRight: false},
  {id: "after_balance", label: "变动后余额", alignRight: false},
  {id: "remark", label: "备注", alignRight: false},
  {id: "create_time", label: "时间", alignRight: false},
];

const QUERY_TYPE_OPTIONS = [
  {value: "coinRecharge", label: "充值金豆"},
  {value: "income", label: "产出金豆"},
];

const CHANGE_TYPE_OPTIONS = [
  {value: "", label: "全部"},
  {value: 1, label: "增加"},
  {value: 2, label: "减少"},
];

const TYPE_COLOR_MAP = {
  1: "success",
  2: "error",
};

export default function DhzTransferLog() {
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [hasQueried, setHasQueried] = useState(false);
  const [filter, setFilter] = useState({
    page: 0,
    size: 20,
    queryType: "coinRecharge",
    uid: "",
    type: "",
    startDate: "",
    endDate: "",
  });

  async function getItems(filterOverride) {
    setItems(null);
    const queryFilter = filterOverride || filter;
    const {data, count} = await asyncRequest({
      path: "/jingxi/dhz-transfer-log/list",
      data: queryFilter,
    });
    setItems(data || []);
    setTotal(count);
    setHasQueried(true);
  }

  const handlePageChange = (event, value, newSize) => {
    let newFilter;
    if (newSize !== undefined) {
      newFilter = {...filter, page: 0, size: newSize};
    } else {
      const newPage = value - 1;
      if (newPage === filter.page) return;
      newFilter = {...filter, page: newPage};
    }
    setFilter(newFilter);
    if (hasQueried) getItems(newFilter);
  };

  function handleReset() {
    setHasQueried(false);
    setItems(null);
    setTotal(0);
    setFilter({
      page: 0,
      size: 20,
      queryType: "coinRecharge",
      uid: "",
      type: "",
      startDate: "",
      endDate: "",
    });
  }

  const isNotFound = items && items.length === 0;

  return (
    <Page title="金豆日志">
      <Container maxWidth="2000px">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            金豆日志查询
          </Typography>
        </Stack>

        <Card>
          <ListToolbar
            showSearch={false}
            onReset={handleReset}
            onRefresh={() => getItems()}
          >
            <Grid container rowGap={2} alignItems="center">
              <Grid item sm={6} md={3}>
                <FormControl size="small">
                  <InputLabel>查询类型</InputLabel>
                  <Select
                    label="查询类型"
                    value={filter.queryType}
                    onChange={(e) => setFilter({...filter, queryType: e.target.value})}
                    style={{width: 200}}
                  >
                    {QUERY_TYPE_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item sm={6} md={3}>
                <TextField
                  size="small"
                  style={{width: 200}}
                  label="鲸世优选UID"
                  placeholder="输入鲸世优选UID"
                  autoComplete="off"
                  value={filter.uid}
                  onChange={(e) => setFilter({...filter, uid: e.target.value})}
                />
              </Grid>
              <Grid item sm={6} md={3}>
                <FormControl size="small">
                  <InputLabel>变动类型</InputLabel>
                  <Select
                    label="变动类型"
                    value={filter.type}
                    onChange={(e) => setFilter({...filter, type: e.target.value})}
                    style={{width: 200}}
                  >
                    {CHANGE_TYPE_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item sm={6} md={3}>
                <TextField
                  size="small"
                  style={{width: 200}}
                  label="开始日期"
                  placeholder="YYYY-MM-DD"
                  autoComplete="off"
                  value={filter.startDate}
                  onChange={(e) => setFilter({...filter, startDate: e.target.value})}
                />
              </Grid>
              <Grid item sm={6} md={3}>
                <TextField
                  size="small"
                  style={{width: 200}}
                  label="结束日期"
                  placeholder="YYYY-MM-DD"
                  autoComplete="off"
                  value={filter.endDate}
                  onChange={(e) => setFilter({...filter, endDate: e.target.value})}
                />
              </Grid>
              <Grid item sm={6} md={3}>
                <Button variant="contained" size="small" onClick={() => getItems()}>
                  查询
                </Button>
              </Grid>
            </Grid>
          </ListToolbar>

          <Scrollbar>
            <TableContainer sx={{minWidth: 900}}>
              <Table>
                <ListHead headLabel={TABLE_HEAD}/>
                {!hasQueried ? (
                  <TableBody>
                    <TableRow>
                      <TableCell colSpan={9}>
                        <Stack sx={{width: "100%", height: 200}} alignItems="center" justifyContent="center">
                          <Typography variant="body2" color="text.secondary">请设置查询条件后点击查询</Typography>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                ) : !items ? (
                  <TableBody>
                    <TableRow>
                      <TableCell colSpan={9}>
                        <Stack sx={{width: "100%", height: 200}} alignItems="center" justifyContent="center">
                          <CircularProgress/>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                ) : (
                  <TableBody>
                    {items.map((row) => {
                      const {
                        id,
                        type,
                        typeName,
                        user_id,
                        plat_id,
                        amount,
                        before_balance,
                        after_balance,
                        remark,
                        create_time,
                      } = row;
                      return (
                        <TableRow hover key={id} tabIndex={-1}>
                          <TableCell align="left">{id}</TableCell>
                          <TableCell align="left">
                            <Chip
                              size="small"
                              label={typeName}
                              color={TYPE_COLOR_MAP[type] || "default"}
                            />
                          </TableCell>
                          <TableCell align="left">{user_id}</TableCell>
                          <TableCell align="left">{plat_id || "-"}</TableCell>
                          <TableCell align="left">
                            <Typography
                              variant="body2"
                              color={type === 1 ? "success.main" : "error.main"}
                            >
                              {type === 1 ? `+${amount}` : `-${amount}`}
                            </Typography>
                          </TableCell>
                          <TableCell align="left">{before_balance ?? "-"}</TableCell>
                          <TableCell align="left">{after_balance ?? "-"}</TableCell>
                          <TableCell align="left">{remark || "-"}</TableCell>
                          <TableCell align="left">
                            {create_time ? moment(create_time).format("YYYY-MM-DD HH:mm:ss") : "-"}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                )}
                {isNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={9} sx={{py: 5}}>
                        <SearchNotFound/>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>

          <MyPagination
            total={total}
            page={filter.page}
            size={filter.size}
            onChange={handlePageChange}
          />
        </Card>
      </Container>
    </Page>
  );
}
